import { DatePicker } from '@syncfusion/ej2-calendars';
import { Dialog } from '@syncfusion/ej2-popups';

import { GDPR_HARD_DELETE_DAYS } from '@/shared/constants';
import { parseIsoDate, shiftFromUtc, shiftToUtc } from '@/shared/utils/date';
import { DEFAULT_LOCALE, dateInputFormat, formatDate, formatEnumKey } from '@/shared/utils/format';
import { escapeHtml } from '@/shared/utils/misc';
import { utcAddDays, utcToday } from '@/shared/utils/utc';

export type GdprErasureModalAccount = {
  name: string;
  status: string;
  trialExpiresTime?: string | null;
  subscriptionExpiresTime?: string | null;
  subscriptionCancelledTime?: string | null;
  deletionScheduledTime?: string | null;
};

let dialog: Dialog | null = null;
let hardDeleteDatePicker: DatePicker | null = null;
let confirmInput: HTMLInputElement | null = null;
let currentAccount: GdprErasureModalAccount | null = null;
let resolvePromise: ((value: Date | null) => void) | null = null;

export const isActivePayingCustomer = (status: string) => status === 'Subscription';

export const requiresSubscriptionCancellationFirst = (account: GdprErasureModalAccount) =>
  isActivePayingCustomer(account.status) && !account.subscriptionCancelledTime;

export const earliestHardDeleteDate = (account: GdprErasureModalAccount) => {
  const today = utcToday();
  const subscriptionCancelledDate = parseIsoDate(account.subscriptionCancelledTime);
  return subscriptionCancelledDate && subscriptionCancelledDate > today ? subscriptionCancelledDate : today;
};

const formatOptionalDate = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  return isNaN(date.getTime()) ? '-' : formatDate(date);
};

const BLOCKED_MESSAGE =
  '<br/><strong>This account has an active subscription that has not been cancelled yet. Cancel it in Stripe before scheduling hard deletion.</strong>';

const ACTIVE_PAYING_MESSAGE =
  '<br/><strong>This account has an active subscription. Deletion is destructive - type the account name below to confirm.</strong>';

const buildContent = (account: GdprErasureModalAccount) => {
  const activePaying = isActivePayingCustomer(account.status);
  const blocked = requiresSubscriptionCancellationFirst(account);

  return `
  <div class="d-flex flex-column gap-3">
    <div class="alert ${activePaying ? 'alert-danger' : 'alert-warning'} mb-0 small" role="alert">
      Access to this customer account will be revoked immediately. Permanent deletion of the firm's data will run on the date you choose.
      ${blocked ? BLOCKED_MESSAGE : activePaying ? ACTIVE_PAYING_MESSAGE : ''}
    </div>
    <dl class="row mb-0 small">
      <dt class="col-sm-5">Account</dt>
      <dd class="col-sm-7">${escapeHtml(account.name)}</dd>
      <dt class="col-sm-5">Status</dt>
      <dd class="col-sm-7">${escapeHtml(formatEnumKey(account.status))}</dd>
      <dt class="col-sm-5">Trial expires</dt>
      <dd class="col-sm-7">${formatOptionalDate(account.trialExpiresTime)}</dd>
      <dt class="col-sm-5">Subscription expires</dt>
      <dd class="col-sm-7">${formatOptionalDate(account.subscriptionExpiresTime)}</dd>
      <dt class="col-sm-5">Subscription cancelled</dt>
      <dd class="col-sm-7">${formatOptionalDate(account.subscriptionCancelledTime)}</dd>
      <dt class="col-sm-5">Inactivity deletion scheduled</dt>
      <dd class="col-sm-7">${formatOptionalDate(account.deletionScheduledTime)}</dd>
    </dl>
    ${
      blocked
        ? ''
        : `<div class="d-flex flex-column gap-1">
      <label class="form-label mb-0" for="gdprHardDeleteDate">Hard deletion date:</label>
      <input id="gdprHardDeleteDate" />
    </div>
    ${
      activePaying
        ? `<div class="d-flex flex-column gap-1">
      <label class="form-label mb-0" for="gdprErasureConfirmName">Type account name to confirm:</label>
      <input id="gdprErasureConfirmName" class="form-control" autocomplete="off" />
    </div>`
        : ''
    }`
    }
  </div>
`;
};

const settle = (value: Date | null) => {
  if (!resolvePromise) return;
  const resolve = resolvePromise;
  resolvePromise = null;
  resolve(value);
};

const setScheduleEnabled = (enabled: boolean) => {
  const button = document.querySelector<HTMLButtonElement>('#gdprErasureDialog button.e-danger');
  if (button) {
    button.disabled = !enabled;
  }
};

const syncScheduleEnabled = () => {
  if (!currentAccount || requiresSubscriptionCancellationFirst(currentAccount)) {
    setScheduleEnabled(false);
    return;
  }

  if (!isActivePayingCustomer(currentAccount.status)) {
    setScheduleEnabled(true);
    return;
  }

  setScheduleEnabled(confirmInput?.value.trim() === currentAccount.name);
};

const destroyControls = () => {
  hardDeleteDatePicker?.destroy();
  hardDeleteDatePicker = null;
  confirmInput = null;
};

const initControls = () => {
  if (!currentAccount || requiresSubscriptionCancellationFirst(currentAccount)) {
    syncScheduleEnabled();
    return;
  }

  const minDateUtc = earliestHardDeleteDate(currentAccount);
  const normalDefaultUtc = utcAddDays(utcToday(), GDPR_HARD_DELETE_DAYS);
  const defaultDateUtc = minDateUtc > normalDefaultUtc ? minDateUtc : normalDefaultUtc;

  const minDate = shiftFromUtc(minDateUtc);
  const defaultDate = shiftFromUtc(defaultDateUtc);

  hardDeleteDatePicker = new DatePicker({
    locale: DEFAULT_LOCALE.code,
    value: defaultDate,
    min: minDate,
    showClearButton: false,
    showTodayButton: false,
    format: dateInputFormat,
    width: '100%',
  });
  hardDeleteDatePicker.appendTo('#gdprHardDeleteDate');

  confirmInput = document.querySelector<HTMLInputElement>('#gdprErasureConfirmName');
  confirmInput?.addEventListener('input', syncScheduleEnabled);
  syncScheduleEnabled();
};

const confirmSelection = () => {
  if (!hardDeleteDatePicker?.value || !currentAccount || requiresSubscriptionCancellationFirst(currentAccount)) {
    return;
  }

  if (isActivePayingCustomer(currentAccount.status) && confirmInput?.value.trim() !== currentAccount.name) {
    return;
  }

  settle(shiftToUtc(hardDeleteDatePicker.value));
  dialog?.hide();
};

const ensureDialog = () => {
  if (dialog) {
    return dialog;
  }

  const host = document.createElement('div');
  host.id = 'gdprErasureDialog';
  document.body.appendChild(host);

  dialog = new Dialog({
    header: 'Delete customer account data',
    content: '',
    target: document.body,
    showCloseIcon: true,
    isModal: true,
    visible: false,
    width: '560px',
    zIndex: 1035,
    animationSettings: { effect: 'Fade' },
    buttons: [
      {
        click: () => {
          dialog?.hide();
          settle(null);
        },
        buttonModel: { content: 'Cancel', cssClass: 'e-btn e-flat' },
      },
      {
        click: confirmSelection,
        buttonModel: { content: 'Schedule deletion', cssClass: 'e-btn e-danger' },
      },
    ],
    open: () => {
      destroyControls();
      initControls();
      document.querySelector('#gdprErasureDialog button.e-danger')?.classList.remove('e-flat');
    },
    close: () => {
      settle(null);
      destroyControls();
    },
  });

  dialog.appendTo(host);
  return dialog;
};

export const openGdprErasureModal = (account: GdprErasureModalAccount): Promise<Date | null> => {
  return new Promise((resolve) => {
    resolvePromise = resolve;
    currentAccount = account;

    destroyControls();

    const dlg = ensureDialog();
    dlg.content = buildContent(account);
    dlg.dataBind();
    dlg.show();
  });
};
